/**
 * Blasons de l'espace élève — catalogue déclaratif calculé FRONT-ONLY depuis
 * les données déjà disponibles (profil XP + stats d'évaluations). Aucun
 * endpoint dédié : un blason est « acquis » quand sa métrique atteint sa cible.
 *
 * Approximation assumée (documentée) : `flamme_jours` est la série COURANTE,
 * pas le record — sans garde-fou, un blason « Série de 3 jours » se
 * re-verrouillerait quand la flamme retombe. On mémorise donc le maximum vu
 * sur CET appareil (localStorage tanoMaxFlamme).
 */

const MAX_FLAMME_KEY = 'tanoMaxFlamme';

/** Record de flamme vu sur cet appareil (mis à jour à chaque lecture). */
export const maxFlamme = (profil) => {
    const courante = profil?.flamme_jours ?? 0;
    let record = 0;
    try {
        record = parseInt(localStorage.getItem(MAX_FLAMME_KEY) || '0', 10) || 0;
        if (courante > record) {
            record = courante;
            localStorage.setItem(MAX_FLAMME_KEY, String(record));
        }
    } catch { record = courante; }
    return Math.max(record, courante);
};

const nbSkinsDebloques = (profil) => {
    const d = profil?.skins_debloques || {};
    // le plumage « foret » de départ ne compte pas comme un déblocage
    const plumages = (d.plumage || []).filter(id => id !== 'foret');
    return plumages.length + (d.accessoire || []).length + (d.aura || []).length;
};

/**
 * Catalogue — `compute(ctx)` → { valeur, cible } avec
 * ctx = { profil, stats, recordFlamme }. Libellés autoportants, en français.
 */
export const BLASONS = [
    { id: 'premier-quiz', emoji: '🎯', titre: 'Premier envol', desc: 'Terminer ton premier quiz', unite: 'quiz', compute: ({ stats }) => ({ valeur: stats?.completed ?? 0, cible: 1 }) },
    { id: 'cinq-quiz', emoji: '🏃', titre: 'Lancé !', desc: 'Terminer 5 quiz', unite: 'quiz', compute: ({ stats }) => ({ valeur: stats?.completed ?? 0, cible: 5 }) },
    { id: 'dix-quiz', emoji: '🏆', titre: 'Marathonien', desc: 'Terminer 10 quiz', unite: 'quiz', compute: ({ stats }) => ({ valeur: stats?.completed ?? 0, cible: 10 }) },
    { id: 'serie-3', emoji: '🔥', titre: 'Braise du volcan', desc: 'Une série de 3 jours d\'affilée', unite: 'jours', compute: ({ recordFlamme }) => ({ valeur: recordFlamme, cible: 3 }) },
    { id: 'serie-7', emoji: '🌋', titre: 'Souffle de la Fournaise', desc: 'Une série de 7 jours d\'affilée', unite: 'jours', compute: ({ recordFlamme }) => ({ valeur: recordFlamme, cible: 7 }) },
    { id: 'niveau-5', emoji: '⛰️', titre: 'Grimpeur des Cirques', desc: 'Atteindre le niveau 5', unite: 'niveaux', compute: ({ profil }) => ({ valeur: profil?.niveau ?? 0, cible: 5 }) },
    { id: 'niveau-10', emoji: '🏔️', titre: 'Cap sur le Piton', desc: 'Atteindre le niveau 10', unite: 'niveaux', compute: ({ profil }) => ({ valeur: profil?.niveau ?? 0, cible: 10 }) },
    { id: 'xp-500', emoji: '💎', titre: 'Chasseur d\'XP', desc: 'Gagner 500 XP', unite: 'XP', compute: ({ profil }) => ({ valeur: profil?.xp_total ?? 0, cible: 500 }) },
    { id: 'xp-1500', emoji: '👑', titre: 'Trésorier', desc: 'Gagner 1 500 XP', unite: 'XP', compute: ({ profil }) => ({ valeur: profil?.xp_total ?? 0, cible: 1500 }) },
    { id: 'etoiles-10', emoji: '⭐', titre: 'Fidèle au poste', desc: 'Récolter 10 étoiles d\'assiduité', unite: '⭐', compute: ({ profil }) => ({ valeur: profil?.etoiles_total ?? 0, cible: 10 }) },
    { id: 'collectionneur', emoji: '🎨', titre: 'Collectionneur', desc: 'Débloquer 3 skins pour Tano', unite: 'skins', compute: ({ profil }) => ({ valeur: nbSkinsDebloques(profil), cible: 3 }) },
    { id: 'styliste', emoji: '🕶️', titre: 'Styliste', desc: 'Équiper un accessoire à Tano', unite: '', compute: ({ profil }) => ({ valeur: profil?.skin_accessoire ? 1 : 0, cible: 1 }) },
];

/** Évalue un blason : { acquis, progress (0..1), valeur, cible }. */
export const evalBlason = (blason, ctx) => {
    const { valeur, cible } = blason.compute(ctx);
    return {
        acquis: valeur >= cible,
        progress: cible > 0 ? Math.min(1, valeur / cible) : 0,
        valeur: Math.min(valeur, cible),
        cible,
    };
};
